import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { Download, History } from 'lucide-react';
import {
  LEGACY_DOCUMENT_KINDS,
  PERMISSIONS,
  type DocumentType,
  type LegacyDocumentDetail,
  type LegacyDocumentSummary,
} from '@erp/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usePermissions } from '@/features/auth';
import { InlineHelp } from '@/features/help';
import {
  useLegacyDocument,
  useLegacyDocuments,
  useLegacyDownload,
} from '../api/use-legacy-documents';
import { formatSourceDecimal } from '../lib/format-source-decimal';
import { LexwareArchiveImportPanel } from '../components/lexware-archive-import-panel';

const PAGE_SIZE = 25;
const ITEM_PAGE_SIZE = 50;

function Loading() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" aria-label={t('legacy.loading')} className="space-y-3">
      <span className="sr-only">{t('legacy.loading')}</span>
      {[0, 1, 2].map((key) => (
        <Skeleton key={key} className="h-14 w-full" />
      ))}
    </div>
  );
}

function LoadError({ retry }: { retry: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="space-y-3 rounded-md border border-destructive p-4">
      <p>{t('legacy.error')}</p>
      <Button variant="outline" onClick={retry}>
        {t('legacy.retry')}
      </Button>
    </div>
  );
}

function HistoricalDetail({
  detail,
  documentLink,
  navigationState,
}: {
  detail: LegacyDocumentDetail;
  documentLink: (id: string) => string;
  navigationState: unknown;
}) {
  const { t, i18n } = useTranslation();
  const [itemPage, setItemPage] = useState(1);
  const download = useLegacyDownload(detail.id);
  const value = (input: string | null) =>
    input === null ? t('legacy.unknown') : input === '' ? t('legacy.emptyValue') : input;
  const decimal = (input: string | null) =>
    input === null ? t('legacy.unknown') : formatSourceDecimal(input, i18n.language);
  const snapshot = detail.snapshot;
  const isLexware = snapshot.source.system === 'LEXWARE';
  const calendar = (date: string | null) =>
    date === null
      ? t('legacy.unknown')
      : new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
          new Date(`${date}T00:00:00Z`),
        );
  const itemPages = Math.max(1, Math.ceil(snapshot.items.length / ITEM_PAGE_SIZE));
  const fields = (entries: LegacyDocumentDetail['snapshot']['fields']) => (
    <dl className="grid gap-2 sm:grid-cols-2">
      {entries.map((field) => (
        <div key={field.key}>
          <dt className="text-xs text-muted-foreground">{field.label}</dt>
          <dd className="whitespace-pre-wrap break-words">{value(field.value)}</dd>
        </div>
      ))}
    </dl>
  );
  return (
    <div className="space-y-6 text-sm">
      <Badge variant="outline">
        {t('legacy.origin', {
          system: snapshot.source.system === 'ORGAMAX' ? 'orgaMAX' : snapshot.source.system,
        })}
      </Badge>
      <p className="rounded-md border bg-muted/30 p-3">
        {t(isLexware ? 'legacy.lexwareReadOnly' : 'legacy.readOnly')}
      </p>
      <dl className="grid grid-cols-2 gap-4">
        <div>
          <dt className="text-muted-foreground">{t('legacy.kind')}</dt>
          <dd>{t(`legacy.kinds.${snapshot.kind}`)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('legacy.date')}</dt>
          <dd>{calendar(snapshot.documentDate)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {t(isLexware ? 'legacy.pdfTextStatus' : 'legacy.status')}
          </dt>
          <dd>
            {isLexware
              ? t(`legacy.textStatuses.${snapshot.status.code ?? 'UNKNOWN'}`, {
                  defaultValue: t('legacy.unknown'),
                })
              : (snapshot.status.label ??
                (snapshot.status.code === null
                  ? t('legacy.unknown')
                  : t('legacy.code', { code: snapshot.status.code })))}
          </dd>
        </div>
        {!isLexware && (
          <div>
            {t(
              snapshot.status.open === null
                ? 'legacy.unknownOpen'
                : snapshot.status.open
                  ? snapshot.status.openBasis === 'SOURCE_PAYMENT_STATE'
                    ? 'legacy.paymentOpen'
                    : 'legacy.open'
                  : snapshot.status.openBasis === 'SOURCE_PAYMENT_STATE'
                    ? 'legacy.paymentClosed'
                    : 'legacy.closed',
            )}
          </div>
        )}
      </dl>
      {!isLexware && (snapshot.status.dimensions?.length ?? 0) > 0 && (
        <section>
          <h3 className="mb-2 font-semibold">{t('legacy.statusDimensions')}</h3>
          <dl className="grid gap-3 sm:grid-cols-2">
            {snapshot.status.dimensions?.map((dimension) => (
              <div key={dimension.key}>
                <dt className="text-muted-foreground">{t(`legacy.statusAxes.${dimension.key}`)}</dt>
                <dd>
                  {dimension.sourceLabel ?? t(`legacy.statusMeanings.${dimension.meaning}`)} ·{' '}
                  {value(dimension.code)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <section>
        <h3 className="mb-2 font-semibold">{t('legacy.partner')}</h3>
        {snapshot.partner.nameLines.map((line, index) => (
          <p key={`name-${index}`}>{value(line)}</p>
        ))}
        {snapshot.partner.addressLines.map((line, index) => (
          <p key={`address-${index}`}>{value(line)}</p>
        ))}
        {isLexware && (
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">{t('legacy.partnerNumber')}</dt>
              <dd className="break-words font-mono">{value(snapshot.partner.sourceKey)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('legacy.historicalSourceTenant')}</dt>
              <dd className="break-words">{snapshot.source.tenant}</dd>
            </div>
          </dl>
        )}
        {!isLexware && detail.partnerId !== null && (
          <Link
            className="mt-2 inline-block text-primary underline"
            to={`/partners/${detail.partnerId}`}
          >
            {t('legacy.openPartner')}
          </Link>
        )}
      </section>
      <dl className="grid grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-3">
        {(['net', 'tax', 'gross'] as const).map((key) => (
          <div key={key}>
            <dt className="text-muted-foreground">{t(`legacy.${key}`)}</dt>
            <dd className="break-words font-mono tabular-nums">
              {decimal(snapshot[key])} {snapshot[key] === null ? '' : snapshot.currency}
            </dd>
          </div>
        ))}
      </dl>
      {snapshot.headerTexts.length > 0 && (
        <section>
          <h3 className="mb-2 font-semibold">
            {t(
              isLexware
                ? snapshot.status.code === 'TRUNCATED'
                  ? 'legacy.pdfTextExcerpt'
                  : 'legacy.pdfFullText'
                : 'legacy.headerTexts',
            )}
          </h3>
          {isLexware && <p className="mb-3 text-muted-foreground">{t('legacy.pdfTextHint')}</p>}
          {snapshot.headerTexts.map((text, index) => (
            <p
              className={
                isLexware
                  ? 'mb-3 whitespace-pre-wrap break-words rounded-md border bg-muted/20 p-4 font-mono text-sm leading-relaxed'
                  : 'whitespace-pre-wrap break-words'
              }
              key={index}
            >
              {text}
            </p>
          ))}
        </section>
      )}
      {!isLexware && (
        <section>
          <h3 className="mb-2 font-semibold">
            {t('legacy.items')} ({snapshot.items.length})
          </h3>
          <ol className="space-y-3">
            {snapshot.items
              .slice((itemPage - 1) * ITEM_PAGE_SIZE, itemPage * ITEM_PAGE_SIZE)
              .map((item) => (
                <li key={item.position} className="space-y-2 rounded-md border p-3">
                  <p className="font-medium">
                    {t('legacy.position')} {value(item.sourcePosition)} ·{' '}
                    {value(item.articleNumber)}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{value(item.description)}</p>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <div>
                      <dt className="text-muted-foreground">{t('legacy.quantity')}</dt>
                      <dd>
                        {decimal(item.quantity)} {value(item.unitLabel)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('legacy.unitPrice')}</dt>
                      <dd>{decimal(item.unitNet)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('legacy.discount')}</dt>
                      <dd>
                        {decimal(item.discountPercent)} {item.discountPercent === null ? '' : '%'}
                      </dd>
                    </div>
                    {(['net', 'tax', 'gross'] as const).map((key) => (
                      <div key={key}>
                        <dt className="text-muted-foreground">{t(`legacy.${key}`)}</dt>
                        <dd>{decimal(item[key])}</dd>
                      </div>
                    ))}
                  </dl>
                  {fields(item.fields)}
                </li>
              ))}
          </ol>
          {itemPages > 1 && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                disabled={itemPage === 1}
                onClick={() => setItemPage((page) => page - 1)}
              >
                {t('legacy.previous')}
              </Button>
              <span>{t('legacy.page', { page: itemPage, total: itemPages })}</span>
              <Button
                variant="outline"
                disabled={itemPage === itemPages}
                onClick={() => setItemPage((page) => page + 1)}
              >
                {t('legacy.next')}
              </Button>
            </div>
          )}
        </section>
      )}
      {!isLexware && (
        <section>
          <h3 className="mb-2 font-semibold">{t('legacy.fields')}</h3>
          {detail.restrictedFields ? (
            <p className="text-muted-foreground">{t('legacy.fieldsRestricted')}</p>
          ) : (
            fields(snapshot.fields)
          )}
        </section>
      )}
      {!isLexware && (
        <section>
          <h3 className="mb-2 font-semibold">{t('legacy.payments')}</h3>
          {detail.restrictedPayments ? (
            <p className="text-muted-foreground">{t('legacy.paymentsRestricted')}</p>
          ) : (
            <ul className="space-y-2">
              {snapshot.payments.map((payment) => (
                <li key={JSON.stringify(payment.source)} className="rounded-md border p-2">
                  <p>
                    {calendar(payment.date)} · {decimal(payment.amount)} ·{' '}
                    {value(payment.sourceKind)}
                  </p>
                  <p className="whitespace-pre-wrap">{value(payment.description)}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {!isLexware && detail.relations.length > 0 && (
        <section>
          <h3 className="mb-2 font-semibold">{t('legacy.relations')}</h3>
          <ul className="space-y-2">
            {detail.relations.map((relation) => (
              <li key={relation.id}>
                <Link
                  className="text-primary underline"
                  to={documentLink(relation.document.id)}
                  state={navigationState}
                >
                  {t(`legacy.kinds.${relation.document.kind}`)} · {value(relation.document.number)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <h3 className="mb-2 font-semibold">{t('legacy.originals')}</h3>
        {detail.artifacts.length === 0 && (
          <p className="text-muted-foreground">{t('legacy.noOriginals')}</p>
        )}
        {(snapshot.attachmentIssues ?? []).map((issue) => (
          <p key={JSON.stringify(issue.reference)} className="text-muted-foreground">
            {t('legacy.emptyFile')} · {value(issue.originalName)}
          </p>
        ))}
        <ul className="space-y-2">
          {detail.artifacts.map((artifact) => (
            <li key={artifact.id} className="rounded-md border p-3">
              <p className="break-words">{artifact.originalName}</p>
              <p className="text-xs text-muted-foreground">
                {t(artifact.role === 'ORIGINAL' ? 'legacy.original' : 'legacy.attachment')}
                {artifact.bytes === 0 ? ` · ${t('legacy.emptyFile')}` : ''}
              </p>
              <Button
                className="mt-2"
                variant="outline"
                disabled={download.isPending}
                onClick={() =>
                  download.mutate(artifact, {
                    onSuccess: () => toast.success(t('legacy.downloaded')),
                    onError: () => toast.error(t('legacy.downloadError')),
                  })
                }
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {t('legacy.download', { name: artifact.originalName })}
              </Button>
            </li>
          ))}
        </ul>
        {download.isError && (
          <p role="alert" className="mt-2 text-destructive">
            {t('legacy.downloadError')}
          </p>
        )}
      </section>
      <details className="rounded-md border p-3">
        <summary className="cursor-pointer font-medium">{t('legacy.provenance')}</summary>
        <dl className="mt-3 space-y-2">
          {(
            [
              ['sourceSystem', snapshot.source.system],
              ['sourceInstance', snapshot.source.instance],
              ['sourceTenant', snapshot.source.tenant],
              ['sourceId', `${snapshot.source.entity} · ${snapshot.source.key}`],
            ] as const
          ).map(([key, text]) => (
            <div key={key}>
              <dt className="text-muted-foreground">{t(`legacy.${key}`)}</dt>
              <dd className="break-all font-mono">{text}</dd>
            </div>
          ))}
        </dl>
        {snapshot.fields.length > 0 && (
          <dl className="mt-3 space-y-2">
            {snapshot.fields.map((field) => (
              <div key={field.key}>
                <dt>{field.label}</dt>
                <dd className="break-words text-muted-foreground">
                  {t('migration.sourceField')}: <code>{field.key}</code>
                </dd>
                {field.definitionSource && (
                  <dd className="break-words text-muted-foreground">
                    {t('migration.sourceDefinition')}: {field.definitionSource.entity} ·{' '}
                    {field.definitionSource.key}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        )}
      </details>
    </div>
  );
}

export function LegacyDocumentsRoute(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <LegacyDocumentsView documentId={id} />;
}

export function PartnerLegacyDocuments({ partnerId }: { partnerId: string }): JSX.Element {
  const [search] = useSearchParams();
  return (
    <LegacyDocumentsView
      key={partnerId}
      partnerId={partnerId}
      documentId={search.get('legacyDocument') ?? undefined}
    />
  );
}

interface CategoryHistoryProps {
  category: DocumentType | 'ALL' | DocumentType[];
  search: string;
  dateFrom: string;
  dateTo: string;
  sort: string;
  ready: boolean;
  operativeFiltersActive: boolean;
}

export function CategoryLegacyDocuments(props: CategoryHistoryProps): JSX.Element {
  const [search] = useSearchParams();
  return (
    <LegacyDocumentsView category={props} documentId={search.get('legacyDocument') ?? undefined} />
  );
}

function LegacyDocumentsView({
  partnerId,
  category,
  documentId: id,
}: {
  partnerId?: string;
  category?: CategoryHistoryProps;
  documentId: string | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scoped = partnerId !== undefined || category !== undefined;
  const paramKey = (key: string) =>
    scoped ? `legacy${key[0]?.toUpperCase()}${key.slice(1)}` : key;
  const filterScope =
    category === undefined
      ? undefined
      : JSON.stringify([
          category.category,
          category.search,
          category.dateFrom,
          category.dateTo,
          category.sort,
        ]);
  // A page belongs to the exact category/filter combination, including after reload.
  const page =
    category !== undefined && search.get('legacyScope') !== filterScope
      ? 1
      : Math.max(1, Number(search.get(paramKey('page'))) || 1);
  const sourceSystem = search.get(paramKey('sourceSystem')) ?? '';
  const isLexwareArchive = sourceSystem === 'LEXWARE';
  const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  for (const key of ['sourceSystem', 'sourceTenant']) {
    const value = search.get(paramKey(key));
    if (value) query.set(key, value);
  }
  for (const key of category === undefined ? ['q', 'kind', 'partnerId', 'productId'] : []) {
    const value = search.get(paramKey(key));
    if (value) query.set(key, value);
  }
  if (partnerId !== undefined) query.set('partnerId', partnerId);
  if (category !== undefined) {
    query.set(
      'category',
      Array.isArray(category.category) ? category.category.join(',') : category.category,
    );
    if (category.search.trim()) query.set('q', category.search.trim());
    if (category.dateFrom) query.set('dateFrom', category.dateFrom);
    if (category.dateTo) query.set('dateTo', category.dateTo);
    if (
      ['documentDate:asc', 'documentDate:desc', 'number:asc', 'number:desc'].includes(category.sort)
    )
      query.set('sort', category.sort);
  }
  const queryString = query.toString();
  const readAllowed = can(PERMISSIONS.DOCUMENT_READ) || can(PERMISSIONS.PURCHASE_READ);
  const list = useLegacyDocuments(queryString, readAllowed && (category?.ready ?? true));
  const detail = useLegacyDocument(readAllowed ? id : undefined);
  const documentLink = useCallback(
    (documentId: string) => {
      if (!scoped) return `/legacy-documents/${documentId}?${queryString}`;
      const next = new URLSearchParams(search);
      next.set('legacyDocument', documentId);
      return `${location.pathname}?${next}`;
    },
    [scoped, location.pathname, queryString, search],
  );
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(paramKey(key), value);
    else next.delete(paramKey(key));
    if (key !== 'page') next.delete(paramKey('page'));
    if (key === 'sourceSystem') next.delete(paramKey('sourceTenant'));
    if (filterScope !== undefined) next.set('legacyScope', filterScope);
    setSearch(next, { state: scoped ? location.state : undefined });
  };
  const reset = () => {
    if (!scoped) {
      const next = new URLSearchParams();
      if (sourceSystem) next.set('sourceSystem', sourceSystem);
      setSearch(next);
      return;
    }
    const next = new URLSearchParams(search);
    for (const key of ['q', 'kind', 'page', 'sourceTenant']) next.delete(paramKey(key));
    setSearch(next, { state: scoped ? location.state : undefined });
  };
  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      if (
        id ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        (event.target instanceof HTMLElement &&
          event.target.closest('input,textarea,select,[contenteditable="true"]'))
      )
        return;
      if (event.key === '/' && category === undefined) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === 'j' || event.key === 'k') {
        const links = Array.from(
          listRef.current?.querySelectorAll<HTMLAnchorElement>('a[data-history-link]') ?? [],
        ).filter((link) => link.getClientRects().length > 0);
        const current = links.findIndex((link) => link === document.activeElement);
        const next =
          event.key === 'j' ? Math.min(links.length - 1, current + 1) : Math.max(0, current - 1);
        links[next]?.focus();
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', keyboard);
    return () => window.removeEventListener('keydown', keyboard);
  }, [id, category]);
  const columns = useMemo<ColumnDef<LegacyDocumentSummary>[]>(
    () => [
      {
        accessorKey: 'number',
        header: t('legacy.number'),
        cell: ({ row }) => (
          <div>
            <Link
              data-history-link
              className="inline-flex min-h-11 items-center text-primary underline"
              to={documentLink(row.original.id)}
              state={scoped ? location.state : undefined}
            >
              {row.original.number ?? t('legacy.unknown')}
            </Link>
            <p className="text-xs text-muted-foreground">
              {t('legacy.origin', {
                system:
                  row.original.source.system === 'ORGAMAX' ? 'orgaMAX' : row.original.source.system,
              })}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'kind',
        header: t('legacy.kind'),
        cell: ({ row }) => t(`legacy.kinds.${row.original.kind}`),
      },
      { accessorKey: 'documentDate', header: t('legacy.date') },
      {
        id: 'partner',
        header: t('legacy.partner'),
        cell: ({ row }) => row.original.partnerNames.join(' · '),
      },
      {
        accessorKey: 'partnerNumber',
        header: t('legacy.partnerNumber'),
        cell: ({ row }) => row.original.partnerNumber ?? t('legacy.unknown'),
      },
      {
        id: 'sourceTenant',
        header: t('legacy.historicalSourceTenant'),
        cell: ({ row }) => row.original.source.tenant,
      },
      {
        id: 'status',
        header: t(isLexwareArchive ? 'legacy.pdfTextStatus' : 'legacy.status'),
        cell: ({ row }) =>
          row.original.source.system === 'LEXWARE'
            ? t(`legacy.textStatuses.${row.original.status.code ?? 'UNKNOWN'}`, {
                defaultValue: t('legacy.unknown'),
              })
            : (row.original.status.label ?? row.original.status.code ?? t('legacy.unknown')),
      },
      {
        accessorKey: 'gross',
        header: t('legacy.gross'),
        cell: ({ row }) =>
          row.original.gross === null
            ? t('legacy.unknown')
            : `${formatSourceDecimal(row.original.gross, i18n.language)} ${row.original.currency}`,
      },
    ],
    [t, i18n.language, documentLink, scoped, location.state, isLexwareArchive],
  );
  const table = useReactTable({
    data: list.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });
  if (!readAllowed)
    return (
      <p role="alert" className="rounded-md border p-4">
        {t('legacy.noAccess')}
      </p>
    );
  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          {scoped ? (
            <h2 className="text-lg font-semibold">
              {t(category ? 'legacy.categoryTitle' : 'legacy.title')}
              {category && list.data ? ` (${list.data.meta.total})` : ''}
            </h2>
          ) : (
            <h1 className="text-xl font-semibold">
              {t(isLexwareArchive ? 'legacy.lexwareTitle' : 'legacy.title')}
            </h1>
          )}
          <p className="text-sm text-muted-foreground">
            {t(
              category
                ? 'legacy.categoryDescription'
                : scoped
                  ? 'legacy.customerDescription'
                  : isLexwareArchive
                    ? 'legacy.lexwareReadOnly'
                    : 'legacy.description',
            )}
          </p>
        </div>
        <InlineHelp
          articleId="sales.legacy-documents"
          anchor={
            isLexwareArchive || detail.data?.snapshot.source.system === 'LEXWARE'
              ? 'lexware-archive'
              : 'overview'
          }
          label={t('legacy.help')}
        />
      </header>
      {category?.operativeFiltersActive && (
        <p className="text-sm text-muted-foreground">{t('legacy.categoryOperativeFilters')}</p>
      )}
      {category &&
        !['documentDate:asc', 'documentDate:desc', 'number:asc', 'number:desc'].includes(
          category.sort,
        ) && <p className="text-sm text-muted-foreground">{t('legacy.categorySort')}</p>}
      {category === undefined && (
        <div className="flex flex-wrap gap-3">
          {!scoped && (
            <>
              <select
                aria-label={t('legacy.sourceSystem')}
                value={sourceSystem}
                onChange={(event) => change('sourceSystem', event.target.value)}
                className="min-h-11 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">{t('legacy.allSources')}</option>
                <option value="ORGAMAX">orgaMAX</option>
                <option value="LEXWARE">{t('legacy.lexwareTitle')}</option>
              </select>
              {isLexwareArchive && (
                <Input
                  className="min-h-11 w-full sm:max-w-44"
                  aria-label={t('legacy.historicalSourceTenant')}
                  placeholder={t('legacy.sourceTenantPlaceholder')}
                  value={search.get('sourceTenant') ?? ''}
                  maxLength={255}
                  onChange={(event) => change('sourceTenant', event.target.value)}
                />
              )}
            </>
          )}
          <Input
            ref={searchRef}
            className="min-h-11 w-full sm:max-w-sm"
            type="search"
            aria-label={t(isLexwareArchive ? 'legacy.lexwareSearch' : 'legacy.search')}
            placeholder={t(isLexwareArchive ? 'legacy.lexwareSearch' : 'legacy.search')}
            value={search.get(paramKey('q')) ?? ''}
            onChange={(event) => change('q', event.target.value)}
          />
          <select
            aria-label={t('legacy.kind')}
            value={search.get(paramKey('kind')) ?? ''}
            onChange={(event) => change('kind', event.target.value)}
            className="min-h-11 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{t('legacy.allKinds')}</option>
            {LEGACY_DOCUMENT_KINDS.filter((kind) =>
              can(
                kind.startsWith('PURCHASE_')
                  ? PERMISSIONS.PURCHASE_READ
                  : PERMISSIONS.DOCUMENT_READ,
              ),
            ).map((kind) => (
              <option key={kind} value={kind}>
                {t(`legacy.kinds.${kind}`)}
              </option>
            ))}
          </select>
          <Button variant="ghost" onClick={reset}>
            {t('legacy.reset')}
          </Button>
        </div>
      )}
      {!scoped && isLexwareArchive && <LexwareArchiveImportPanel />}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <LoadError retry={() => void list.refetch()} />
      ) : list.data?.data.length === 0 ? (
        <div role="status" className="rounded-md border p-6">
          <p>{t('legacy.empty')}</p>
          {category === undefined && (
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => {
                reset();
                searchRef.current?.focus();
              }}
            >
              {t('legacy.reset')}
            </Button>
          )}
        </div>
      ) : (
        <div ref={listRef}>
          <div className="hidden overflow-x-auto rounded-md border md:block">
            <table className="w-full text-sm">
              <thead>
                {table.getHeaderGroups().map((group) => (
                  <tr key={group.id} className="border-b bg-muted/30">
                    {group.headers.map((header) => (
                      <th key={header.id} className="px-3 py-2 text-start font-medium">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="space-y-3 md:hidden">
            {list.data?.data.map((row) => (
              <li key={row.id} className="rounded-md border p-3">
                <Link
                  data-history-link
                  className="inline-flex min-h-11 items-center font-medium text-primary underline"
                  to={documentLink(row.id)}
                  state={scoped ? location.state : undefined}
                >
                  {row.number ?? t('legacy.unknown')}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {t('legacy.origin', {
                    system: row.source.system === 'ORGAMAX' ? 'orgaMAX' : row.source.system,
                  })}
                </p>
                <p>
                  {t(`legacy.kinds.${row.kind}`)} · {row.documentDate ?? t('legacy.unknown')}
                </p>
                <p className="break-words">{row.partnerNames.join(' · ')}</p>
                <dl className="mt-2 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-muted-foreground">{t('legacy.partnerNumber')}</dt>
                    <dd className="break-words font-mono">
                      {row.partnerNumber ?? t('legacy.unknown')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('legacy.historicalSourceTenant')}</dt>
                    <dd className="break-words">{row.source.tenant}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => change('page', String(page - 1))}
            >
              {t('legacy.previous')}
            </Button>
            <span className="text-sm">
              {t('legacy.page', { page, total: Math.max(1, list.data?.meta.totalPages ?? 1) })}
            </span>
            <Button
              variant="outline"
              disabled={page >= (list.data?.meta.totalPages ?? 1)}
              onClick={() => change('page', String(page + 1))}
            >
              {t('legacy.next')}
            </Button>
          </div>
        </div>
      )}
      <Sheet
        open={id !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            if (scoped) {
              const next = new URLSearchParams(search);
              next.delete('legacyDocument');
              setSearch(next, { state: location.state });
            } else navigate(`/legacy-documents?${queryString}`);
          }
        }}
      >
        <SheetContent className="sm:max-w-4xl" closeLabel={t('legacy.close')}>
          <SheetHeader>
            <SheetTitle>{detail.data?.snapshot.number ?? t('legacy.title')}</SheetTitle>
            <SheetDescription>{t('legacy.description')}</SheetDescription>
          </SheetHeader>
          {detail.isPending ? (
            <Loading />
          ) : detail.isError ? (
            <LoadError retry={() => void detail.refetch()} />
          ) : (
            detail.data && (
              <HistoricalDetail
                key={detail.data.id}
                detail={detail.data}
                documentLink={documentLink}
                navigationState={scoped ? location.state : undefined}
              />
            )
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function LegacyHistoryLink({
  partnerId,
  productId,
}: {
  partnerId?: string;
  productId?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { can } = usePermissions();
  if (!can(PERMISSIONS.DOCUMENT_READ) && !can(PERMISSIONS.PURCHASE_READ)) return null;
  const query = new URLSearchParams();
  if (partnerId) query.set('partnerId', partnerId);
  if (productId) query.set('productId', productId);
  return (
    <Button asChild variant="outline" size="sm">
      <Link to={`/legacy-documents?${query}`}>
        <History className="h-4 w-4" aria-hidden="true" />
        {t('legacy.title')}
      </Link>
    </Button>
  );
}
